/**
 * Plan ladder and marketing copy shared by Settings (subscription) and Landing.
 * Plan objects match GET /api/organizations/plans and GET /api/catalog/subscription-plans.
 */

/** Must match Super Admin → Stripe Pricing Matrix and Plans tab Stripe row copy. */
export const STRIPE_ADMIN_SYNC_LABEL = {
  NA_BILLING: 'N/A',
  COMING_SOON: 'Coming Soon',
  PER_SEAT_MANUAL: 'Per-seat (manual)',
  NOT_LINKED: 'Not linked',
  CONNECTED: 'Connected',
  IN_SYNC: 'In Sync',
  OUT_OF_SYNC: 'Out of Sync',
};

/**
 * Single UI rule: catalog `is_coming_soon` (merged from tier_configs on the API).
 * Use everywhere instead of hardcoding tier IDs so Landing, Settings, and admin stay aligned.
 */
export function isPlanComingSoon(plan) {
  return !!(plan && plan.is_coming_soon);
}

/**
 * Settings shopper view: a gifted current plan is not "Coming Soon" for that office.
 * Landing and other shoppers still use isPlanComingSoon.
 */
export function isPlanComingSoonForShopper(plan, currentTier) {
  if (!isPlanComingSoon(plan)) return false;
  const planId = String(plan?.tier_id || plan?.plan_id || '').toLowerCase();
  const current = String(currentTier || '').toLowerCase();
  return planId !== current;
}

/**
 * Short Stripe integration summary for Super Admin → Plans (expanded pricing section).
 * Aligns with STRIPE_ADMIN_SYNC_LABEL / pricing-matrix sync_status.
 */
export function stripeLinkSummaryForPlan(plan) {
  const id = plan?.plan_id ?? plan?.tier_id;
  if (!plan || id == null || id === '') {
    return {
      headline: STRIPE_ADMIN_SYNC_LABEL.NOT_LINKED,
      hint: '',
    };
  }
  if (id === 'free') {
    return {
      headline: STRIPE_ADMIN_SYNC_LABEL.NA_BILLING,
      hint: 'No Stripe product for the free tier.',
    };
  }
  if (plan.is_coming_soon) {
    return {
      headline: STRIPE_ADMIN_SYNC_LABEL.COMING_SOON,
      hint: 'No Stripe prices until this plan is launched.',
    };
  }
  if (plan.pricing?.stripe_product_id) {
    return {
      headline: STRIPE_ADMIN_SYNC_LABEL.CONNECTED,
      hint: '',
    };
  }
  return {
    headline: STRIPE_ADMIN_SYNC_LABEL.NOT_LINKED,
    hint: 'Set catalog prices, then use Stripe Pricing → Push to Stripe.',
  };
}

export const PLAN_TIER_ORDER = ['free', 'basic', 'pro', 'team', 'enterprise'];

export const PLAN_MARKETING_FEATURES = [
  { key: 'unified_view', label: 'Unified dashboard' },
  { key: 'quick_stats_dashboard', label: 'Quick stats on your dashboard' },
  { key: 'email_notifications', label: 'Email notifications' },
  { key: 'document_attachments', label: 'Document attachments on timeline items' },
  { key: 'tc_role_enabled', label: 'Transaction Coordinator role' },
  { key: 'broker_dashboard', label: 'Team home' },
  { key: 'api_access', label: 'API access' },
  { key: 'sso_enabled', label: 'SSO / SAML' },
  { key: 'white_label', label: 'White label branding' },
];

/** Product copy not stored on plan rows; only added for the free tier on marketing. */
export const LANDING_FREE_EXTRAS = [
  'Due dates skip weekends and Hawaii holidays',
  'Reminders before a date is due',
  'Hawaii tax calculators',
];

export function getPreviousTierId(tierId) {
  const i = PLAN_TIER_ORDER.indexOf(tierId);
  return i > 0 ? PLAN_TIER_ORDER[i - 1] : null;
}

export function previousTierPlan(availablePlans, tierId) {
  const prevId = getPreviousTierId(tierId);
  if (!prevId) return null;
  return availablePlans.find((p) => p.tier_id === prevId) || null;
}

export function getPreviousTierDisplayName(availablePlans, tierId) {
  const prev = previousTierPlan(availablePlans, tierId);
  if (prev?.name) return prev.name;
  const prevId = getPreviousTierId(tierId);
  if (!prevId) return '';
  return prevId.charAt(0).toUpperCase() + prevId.slice(1);
}

export function formatTransactionLimit(n) {
  if (n === null || n === undefined) return 'Unlimited active transactions';
  return `Up to ${n} active transactions`;
}

export function formatTeamLimit(n) {
  if (n === null || n === undefined || n >= 999999) return 'Unlimited seats';
  if (n === 1) return 'Solo (1 seat)';
  return `Up to ${n} seats`;
}

export function planListMonthlyPrice(plan) {
  if (plan?.pricing_model === 'base_plus_seats' && plan.base_price_monthly != null && plan.base_price_monthly !== '') {
    return Number(plan.base_price_monthly) || 0;
  }
  return Number(plan?.price_monthly) || 0;
}

export function planListAnnualPrice(plan) {
  if (plan?.pricing_model === 'base_plus_seats' && plan.base_price_annual != null && plan.base_price_annual !== '') {
    return Number(plan.base_price_annual) || 0;
  }
  return Number(plan?.price_annual) || 0;
}

export function planSeatPriceMonthly(plan) {
  if (plan?.pricing_model !== 'base_plus_seats' || plan?.seat_price_monthly == null || plan.seat_price_monthly === '') {
    return null;
  }
  return Number(plan.seat_price_monthly);
}

export function formatIncludedSeats(plan) {
  if (plan?.pricing_model !== 'base_plus_seats') return null;
  const n = Number(plan.included_seats);
  if (!n) return null;
  return n;
}

export function formatTemplateLimit(n) {
  if (n === null || n === undefined) return 'Unlimited transaction templates';
  if (n === 1) return '1 transaction template';
  return `${n} transaction templates`;
}

export function historyRetentionLabel(days) {
  if (days === null || days === undefined) return null;
  const d = Number(days);
  if (Number.isNaN(d)) return null;
  if (d >= 999999) return 'Unlimited history retention';
  if (d >= 365 && d % 365 === 0) {
    const y = d / 365;
    return `${y} year${y === 1 ? '' : 's'} of history`;
  }
  return `${d} days of history`;
}

/**
 * Bullets under plan limits: inherit line, new feature flags vs previous tier, history, billing model, free extras.
 */
/** Coming-soon subtitle. Team is sold as a shared Team, not "for brokerages". */
export function planComingSoonSubtitle(plan) {
  const id = plan?.tier_id || plan?.plan_id;
  if (id === 'enterprise') return 'For large offices';
  if (id === 'team') return 'For your team';
  return '';
}

export function buildPlanMarketingBullets(plans, plan) {
  const prev = previousTierPlan(plans, plan.tier_id);
  const bullets = [];

  const prevId = getPreviousTierId(plan.tier_id);
  if (prevId) {
    const name = getPreviousTierDisplayName(plans, plan.tier_id);
    if (name) bullets.push(`Everything in ${name}`);
  }

  for (const { key, label } of PLAN_MARKETING_FEATURES) {
    if (plan[key] && !(prev && prev[key])) {
      bullets.push(label);
    }
  }

  const h = historyRetentionLabel(plan.history_days);
  const hp = prev ? historyRetentionLabel(prev.history_days) : null;
  if (h && h !== hp) {
    bullets.push(h);
  }

  if (plan.pricing_model === 'base_plus_seats' && (!prev || prev.pricing_model !== 'base_plus_seats')) {
    const included = formatIncludedSeats(plan);
    const seat = planSeatPriceMonthly(plan);
    if (included && seat != null) {
      bullets.push(`${included} people included, then +$${Math.round(seat)} for each extra person each month`);
    } else {
      bullets.push('Per-seat billing');
    }
  }

  if (
    prev &&
    prev.tc_assignments_per_agent != null &&
    (plan.tc_assignments_per_agent === null || plan.tc_assignments_per_agent === undefined)
  ) {
    bullets.push('Unlimited Transaction Coordinator assignments');
  }

  if (plan.tier_id === 'free') {
    bullets.push(...LANDING_FREE_EXTRAS);
  }

  return bullets;
}
